import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountCardListStore, $countryProvincesStore, $countriesStore, $transferCurrenciesStore, $transferReasonsStore } from '@lib/store';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType, TransferRecipientTypeEnum } from '@shared/api';
import { BusinessTab, IndividualTab } from './widgets';
import { PageContainer, SelectAccount } from '@widgets';
import { Icon, Tabs, Text } from '@shared/widgets';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

export const PaymentOtherBankPage = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') as TransferRecipientTypeEnum | null;

  const accountStore = useUnit($accountCardListStore.store);

  const [tab, setTab] = useState<TransferRecipientTypeEnum>(type || TransferRecipientTypeEnum.Individual);
  const [account, setAccount] = useState<AccountResponse | null>(null);

  const { t } = useTranslation('payments');

  useEffect(() => {
    Promise.allSettled([
      $accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment }),
      $transferCurrenciesStore.events.getTransferCurrencies(),
      $countriesStore.events.getCountries(),
      $countryProvincesStore.events.getCountryProvinces(),
      $transferReasonsStore.events.getTransferReasons(),
    ]);
    return () => {
      $accountCardListStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (accountStore.data.length > 0) setAccount(accountStore.data[0]);
  }, [accountStore.data]);

  const TABS = useMemo(
    () => [
      { value: TransferRecipientTypeEnum.Individual, label: t('recipientType.individual', { ns: 'common' }) },
      { value: TransferRecipientTypeEnum.Business, label: t('recipientType.business', { ns: 'common' }) },
    ],
    [t],
  );
  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      <main
        className={cn(
          'flex flex-col gap-6',
          PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        )}
      >
        <Text weight={PaletteUtils.getPaletteParam('semibold')} size='2xl'>
          {t('otherBank.title')}
        </Text>
        <SelectAccount
          label={t('otherBank.placeholders.transferFrom')}
          loading={accountStore.loading}
          type={AccountType.Payment}
          accounts={accountStore.data}
          value={account}
          onChange={setAccount}
        />
        <Tabs
          className={cn('bg-white dark:bg-soft-black', PaletteUtils.isInsigniaPalette && '-mb-6 p-6 pb-0')}
          options={TABS}
          value={tab}
          onChange={setTab}
        />
        {tab == TransferRecipientTypeEnum.Individual && <IndividualTab account={account} />}
        {tab == TransferRecipientTypeEnum.Business && <BusinessTab account={account} />}
      </main>
    </PageContainer>
  );
};
